import React, { useState } from 'react';
import { Card, Button, Row, Col, Badge, ButtonGroup, Alert } from 'react-bootstrap';
import { Demo } from '../types/Demo';
import { apiService } from '../services/api';
import ScreenshotUploadModal from './ScreenshotUploadModal';
import PasswordResetModal from './PasswordResetModal';

interface DemosListProps {
  demos: Demo[];
  onDemoDeleted: () => void;
}

const DemosList: React.FC<DemosListProps> = ({ demos, onDemoDeleted }) => {
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [alert, setAlert] = useState<{ type: 'success' | 'danger' | 'info'; message: string } | null>(null);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeDisplayName = (theme: string) => {
    const themeNames: { [key: string]: string } = {
      'template1': 'Template 1',
      'template2': 'Template 2',
      'template3': 'Template 3'
    };
    return themeNames[theme] || theme;
  };

  const handleCreateSnapshot = (domain: string) => {
    setSelectedDomain(domain);
    setShowModal(true);
  };

  const handleOpenPasswordResetModal = (domain: string) => {
    setSelectedDomain(domain);
    setShowPasswordModal(true);
  };

  const handleDeleteDemo = async (id: number, domain: string) => {
    if (!window.confirm(`"${domain}" demo sitesini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await apiService.deleteDemo(id);
      showAlert('success', `Demo site "${domain}" başarıyla silindi`);
      onDemoDeleted();
    } catch (error) {
      showAlert('danger', 'Demo site silinirken hata oluştu');
    }
  };

  const showAlert = (type: 'success' | 'danger' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleScreenshotUploaded = () => {
    setShowModal(false);
    showAlert('success', 'Screenshot başarıyla yüklendi');
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordModal(false);
    showAlert('success', 'Şifre başarıyla sıfırlandı');
  };

  if (demos.length === 0) {
    return (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">
              <i className="bi bi-list-ul"></i> Demo Siteler
            </h5>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              <i className="bi bi-arrow-clockwise"></i> Yenile
            </Button>
          </div>
          
          {alert && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
              {alert.message}
            </Alert>
          )}

          <div className="text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
            <h5 className="mt-3">Henüz demo site oluşturulmamış</h5>
            <p className="text-muted">İlk demo sitenizi oluşturmak için "Yeni Oluştur" sekmesini kullanın.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">
              <i className="bi bi-list-ul"></i> Demo Siteler
            </h5>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              <i className="bi bi-arrow-clockwise"></i> Yenile
            </Button>
          </div>

          {alert && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
              {alert.message}
            </Alert>
          )}

          {demos.map(demo => (
            <div key={demo.id} className="demo-item">
              <Row className="align-items-center">
                <Col md={8}>
                  <h6 className="mb-1 d-flex align-items-center gap-2">
                    <span><i className="bi bi-globe"></i> {demo.domain}</span>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => handleCreateSnapshot(demo.domain)}
                      title="Snapshot Al"
                    >
                      <i className="bi bi-camera"></i>
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => handleOpenPasswordResetModal(demo.domain)}
                      title="Şifre Sıfırla"
                    >
                      <i className="bi bi-key"></i>
                    </Button>
                  </h6>
                  <p className="mb-1 text-muted">
                    <i className="bi bi-envelope"></i> {demo.email}
                  </p>
                  <div className="d-flex gap-2 align-items-center">
                    <Badge bg="primary">
                      {(demo as any).snapshotName || getThemeDisplayName(demo.theme)}
                    </Badge>
                    <small className="text-muted">
                      <i className="bi bi-calendar"></i> {formatDate(demo.createdAt)}
                    </small>
                  </div>
                </Col>
                <Col md={4} className="text-end">
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-primary"
                      as="a"
                      href={demo.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-eye"></i> Görüntüle
                    </Button>
                    <Button
                      variant="outline-secondary"
                      as="a"
                      href={`${demo.demoUrl}/panel`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-gear"></i> Panel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteDemo(demo.id, demo.domain)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>
            </div>
          ))}
        </Card.Body>
      </Card>

      <ScreenshotUploadModal
        show={showModal}
        onHide={() => setShowModal(false)}
        domain={selectedDomain}
        onScreenshotUploaded={handleScreenshotUploaded}
      />

      <PasswordResetModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        domain={selectedDomain}
        onPasswordReset={handlePasswordResetSuccess}
      />
    </>
  );
};

export default DemosList;